import React, { useState, useEffect, useCallback } from 'react';
import { Dropdown } from "@voicecare.ai/ui-library";
import { callAutomationService } from '../../services/callAutomationService';

const FacilitySelection = ({ 
  value = '', 
  onChange, 
  placeholder = "Select Group / Facility NPI",
  themeMode = 'dark',
  currentTheme = {},
  style = {},
  disabled = false,
  treatmentAddress = ''
}) => {
  const [facilities, setFacilities] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Load facilities data
  const loadData = useCallback(async () => {
    if (facilities.length === 0) {
      setIsLoading(true);
      try {
        const facilitiesData = await callAutomationService.getFacilities();
        setFacilities(facilitiesData);
        console.log('🏢 Loaded facilities data:', facilitiesData.length, 'facilities');
      } catch (error) {
        console.error('❌ Error loading facilities:', error);
      } finally {
        setIsLoading(false);
      }
    }
  }, [facilities.length]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Normalize helper to match addresses robustly
  const normalize = (str) => (str || '').toString().replace(/,/g, ' ').replace(/\s+/g, ' ').trim().toLowerCase();

  // Convert facilities to dropdown options format (add address when duplicates exist)
  const facilityOptions = (() => {
    const byNpiCount = facilities.reduce((acc, f) => {
      acc[f.value] = (acc[f.value] || 0) + 1;
      return acc;
    }, {});
    return facilities.map(facility => {
      const base = `${facility.facilityName}\nNPI: ${facility.value}`;
      const addr = facility.formattedAddress || `${facility.location?.streetAddress || ''} ${facility.location?.city || ''} ${facility.location?.state || ''} ${facility.location?.zipcode || ''}`.trim();
      const label = byNpiCount[facility.value] > 1 ? `${base}\n${addr}` : base;
      // Use composite unique value for selection; keep _npi for matching display state
      const composite = `${facility.value}::${facility.id}`;
      return { label, value: composite, _npi: facility.value, _id: facility.id, _addr: addr };
    });
  })();

  // Find the selected option; when multiple facilities share NPI, prefer the one matching treatmentAddress
  const selectedOption = (() => {
    // If value is already composite, prefer exact match
    if (typeof value === 'string' && value.includes('::')) {
      return facilityOptions.find(o => o.value === value) || null;
    }
    // Otherwise match by NPI, and disambiguate by Treatment Address
    const options = facilityOptions.filter(o => o._npi === value);
    if (options.length <= 1) return options[0] || null;
    const normalizedTa = normalize(treatmentAddress);
    const match = options.find(o => normalize(o._addr) === normalizedTa);
    return match || options[0] || null;
  })();

  const handleChange = (selectedOption) => {
    if (selectedOption) {
      // Emit composite value for unique selection; parent will persist NPI only if needed
      onChange(selectedOption.value);
    } else {
      onChange('');
    }
  };

  return (
    <div style={{ position: 'relative', ...style }}>
      <Dropdown
        options={facilityOptions}
        value={selectedOption}
        onChange={handleChange}
        placeholder={isLoading ? "Loading facilities..." : placeholder}
        themeMode={themeMode}
        disabled={disabled || isLoading}
        isSearchable={true}
        width="100%"
        menuPlacement="auto"
      />
    </div>
  );
};

export default FacilitySelection;